import { useState } from "react";
import { ClientsDashboard } from "../../../components/leads/ClientsDashboard";
import { ScopeOfWorkView } from "../../../components/leads/ScopeOfWorkView";
import { QuotationView } from "../../../components/leads/QuotationView";
import { LeadView } from "../../../components/leads/LeadView";
import { User, FileText, FileSpreadsheet } from "lucide-react";
import RequireAccess from "components/RequireAccess";

export default function LeadManagement({ currentMember }) {
    const [selectedClient, setSelectedClient] = useState(null);
    const [activeTab, setActiveTab] = useState("lead");
    const isOwnerOrAdmin = currentMember?.joined_as === "owner" || currentMember?.is_admin === true;
    const hasLeadApprover = isOwnerOrAdmin || currentMember?.lead_approver === true;
    const hasLeadAccess =
        isOwnerOrAdmin || currentMember?.lead_control === true || currentMember?.lead_approver === true;

    const handleSelectClient = (client) => {
        setSelectedClient(client);
        if (client.status === "active") {
            setActiveTab("scope");
        } else if (client.status === "quotation") {
            setActiveTab("quotation");
        } else {
            setActiveTab("lead");
        }
    };

    const handleCreateQuotation = (scopeId) => {
        setActiveTab("quotation");
    };

    const handleGoToScope = () => {
        setActiveTab("scope");
    };

    const handleGoToQuotation = () => {
        setActiveTab("quotation");
    };
    if (!hasLeadAccess) {
        return (
            <RequireAccess
                module="Lead Management"
                currentMember={currentMember}
                permission="lead_control"
            />
        );
    }

    if (!selectedClient) {
        return (
            <div className="h-[92vh] overflow-y-scroll p-4 w-[85%]">
                <ClientsDashboard onSelectClient={handleSelectClient} />
            </div>
        );
    }

    const tabs = [
        { id: "lead", label: "Lead View", icon: User },
        { id: "scope", label: "Scope of Work", icon: FileText },
        { id: "quotation", label: "Quotation", icon: FileSpreadsheet },
    ];

    return (
        <div className="h-[92vh] overflow-y-scroll flex flex-col p-4 w-[85%]">
            {/* Tab Navigation - Compact */}
            <div className="flex items-center gap-1 mb-3 border-b border-border">
                <button
                    onClick={() => setSelectedClient(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5"
                >
                    ← Dashboard
                </button>
                <span className="text-xs font-medium text-foreground px-2 py-1 bg-primary/5 rounded">{selectedClient.name}</span>
                <div className="flex-1" />
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px ${activeTab === tab.id
                            ? "border-sm-primary text-sm-primary"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                    >
                        <tab.icon className="w-3.5 h-3.5" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === "lead" && (
                    <LeadView
                        client={selectedClient}
                        onCreateScope={handleGoToScope}
                        onCreateQuotation={handleGoToQuotation}
                    />
                )}
                {activeTab === "scope" && (
                    <ScopeOfWorkView
                        client={selectedClient}
                        onCreateQuotation={handleCreateQuotation}
                        canApprove={hasLeadApprover}
                        canViewQuotation={hasLeadApprover}
                    />
                )}
                {activeTab === "quotation" && (
                    <QuotationView client={selectedClient} canApprove={hasLeadApprover} />
                )}
            </div>
        </div>
    );
}